import { Link } from './Link';
import { learnFor } from '../pages/public/learn/learnArticles';

/**
 * The `/tools/*` and `/features/*` side of the `/learn/` link matrix.
 *
 * Renders nothing unless the registry declares a pairing for this path, so a page with no genuine
 * counterpart stays as it was rather than getting a weaker link. `learnFor()` inverts the `tool` /
 * `feature` / `alsoServes` declarations, so a pairing is written once and both directions follow.
 *
 * THREE DELIBERATE CHOICES, all about what a link is actually worth:
 *
 * 1. **Anchor text is the article title**, never "learn more". The anchor is the strongest signal
 *    a link carries about what the target is about; a generic one throws that away and leaves a
 *    link that only moves PageRank.
 * 2. **It sits in the content flow**, directly under the tool body / feature pillars — not in a
 *    footer rail. Templated site-wide link blocks are discounted heavily; a contextual link where
 *    a reader would actually want it is not.
 * 3. **It is NOT marked `data-linkrail`.** That attribute exempts navigation from the
 *    near-duplicate guard in test/seo.spec.ts. This is content, so it should be measured like
 *    content — if these blocks ever grow into something that makes two pages look alike, the
 *    guard is supposed to notice.
 */
export function LearnCrossLink({ path }: { path: string }) {
  const articles = learnFor(path);
  if (articles.length === 0) return null;

  return (
    <section className="mt-12 rounded-2xl border border-bordersoft bg-panel/60 p-6">
      <h2 className="font-display text-[17px] font-bold tracking-tight">
        {articles.length > 1 ? 'Background reading' : 'Not sure what this means?'}
      </h2>
      <ul className="mt-4 flex flex-col gap-3">
        {articles.map((a) => (
          <li key={a.slug} className="text-[13.5px] leading-relaxed text-muted">
            <Link
              to={`/learn/${a.slug}`}
              className="font-semibold text-accent underline underline-offset-4"
            >
              {a.title}
            </Link>
            <span className="ml-1.5">— {a.blurb}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
